import "./components/camping/ImageZoomDialog.css";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import FishingPermits from "./pages/FishingPermits";
import CampingPermits from "./pages/CampingPermits";
import HuntingPermits from "./pages/HuntingPermits";
import BookingPage from "./pages/BookingPage";
import CartPage from "./pages/CartPage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import AdminUserManagement from "./pages/AdminUserManagement";
import BackfillProcessingFees from "./pages/BackfillProcessingFees";
import { CartProvider } from "./contexts/CartContext";
import { SiteAvailabilityProvider } from "./contexts/SiteAvailabilityContext";
import { useEffect, useRef } from "react";
import { runDatabaseMigration } from "./utils/runDatabaseMigration";
import { ScrollToTop } from "./utils/scrollUtils";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  // Track if migration has run
  const migrationRun = useRef(false);
  
  // Run database migration when app starts - only once
  useEffect(() => {
    const runMigration = async () => {
      // Only run once per session
      if (migrationRun.current) {
        return;
      }
      
      migrationRun.current = true;
      
      try {
        console.log("Starting database migration on app load...");
        const result = await runDatabaseMigration();
        console.log("Database migration completed with result:", result);
      } catch (error) {
        console.error("Failed to run database migration:", error);
      }
    };
    
    runMigration();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <CartProvider>
          <SiteAvailabilityProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <ScrollToTop />
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/permits/fishing" element={<FishingPermits />} />
                <Route path="/permits/camping" element={<CampingPermits />} />
                <Route path="/permits/hunting" element={<HuntingPermits />} />
                <Route path="/booking/:siteType/:siteId" element={<BookingPage />} />
                <Route path="/cart" element={<CartPage />} />
                <Route path="/payment-success" element={<PaymentSuccessPage />} />
                <Route path="/admin/users" element={<AdminUserManagement />} />
                <Route path="/backfill-processing-fees" element={<BackfillProcessingFees />} />
                {/* Redirects for common refresh issues */}
                <Route path="/permits" element={<Navigate to="/" replace />} />
                <Route path="/booking" element={<Navigate to="/" replace />} />
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </BrowserRouter>
          </SiteAvailabilityProvider>
        </CartProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
